/**
 * GENERATED by /docs-writer from docs/securelink-tcp-relay.md — DO NOT EDIT BY HAND.
 * Edit the markdown and run `/docs-writer update docs/securelink-tcp-relay.md`.
 *
 * source-sha: f8602eabad4e29ac5980716b24cbcc11112147ad
 * sources (from the markdown's "Sources" section):
 *   - Relay: wg-relay main at de17f56, internal/wsrelay/wsrelay.go, internal/devicesig/devicesig.go,
 *     cmd/wg-relay/main.go, .optisigns/k8s-deployment/prod/, docs/runbooks/wg-relay-k8s.md, README.md
 *   - Policy: optisigns-mdm-api main at ec2e50d, src/services/cacheService.ts, src/services/securelinkService.ts,
 *     src/routes/securelink.ts
 *   - Client: optisigns-android-utils merge request 35 at 335135b, data/remote/RestApi.kt,
 *     data/socket/SocketServerFactory.kt, service/TransportSelector.kt, service/WsRelayBridge.kt, service/RelayAuth.kt, service/WireGuardService.kt, app/env/production.properties,
 *     app/env/qa3e.properties
 *   - Clients without the relay: optiedge-agent main at 0e1b93a and optisigns-vnc-server branch
 *     mdm_yocto_wireguard, searched on 2026-10-14; lab devices on Android Util 4.55 and Pro Util 3.0.10.P27
 *     with the hub addresses blocked, 2026-10-14
 *   - Switch, return, latency, throughput and Remote Control figures: tests on lab devices against QA and
 *     production, 2026-10-12 to 2026-10-14
 *   - Capacity and restart figures: load test on the QA cluster, 2026-10-12
 *   - Production state: read-only checks on the production clusters and the policy key, 2026-10-13 and 2026-10-14
 */
import { Link } from 'react-router';
import { DocSequence } from '../components/DocSequence';
import { DocTopology } from '../components/DocTopology';
import { DocsToc } from '../components/DocsToc';

const TOC = [
  { id: 'when-a-device-uses-the-relay', label: 'When a device uses the relay' },
  { id: 'how-the-relay-path-works', label: 'How the relay path works' },
  { id: 'how-a-device-switches-and-comes-back', label: 'How a device switches and comes back' },
  { id: 'who-is-allowed-onto-the-relay', label: 'Who is allowed onto the relay' },
  { id: 'which-clients-carry-the-relay', label: 'Which clients carry the relay' },
  { id: 'what-to-expect-on-the-relay', label: 'What to expect on the relay' },
  { id: 'known-limits', label: 'Known limits' },
];

export function SecurelinkTcpRelayDocsPage() {
  return (
    <>
      <div className="page doc" id="top">
        <p className="doc-eyebrow">Network · Concept</p>
        <h1 className="doc-title">SecureLink TCP relay</h1>
        <p className="doc-lede">
          The TCP relay carries a device&rsquo;s WireGuard tunnel over <strong>TCP port 443</strong> when the
          device&rsquo;s network drops UDP port 443. The device opens a WebSocket to a device API host, and the
          relay there forwards each WireGuard packet to the device&rsquo;s hub. The device keeps its key, its
          overlay address and its hub. The relay is a fallback. A device starts on UDP and goes back to UDP when
          UDP works again. On 14 October 2026 the relay runs in production and is switched on for two lab devices.
        </p>
        <p className="doc-meta">
          Updated <time dateTime="2026-10-14">14 Oct 2026</time>
        </p>

        <h2 className="doc-h2" id="when-a-device-uses-the-relay">When a device uses the relay</h2>
        <p className="doc-p">A device moves to the relay when all three of these are true.</p>
        <ul className="doc-list">
          <li>
            <strong>Its network drops UDP 443 to the hubs and allows TCP 443 to the device API.</strong> This is the
            network of a customer whose firewall does not list the hub addresses. The relay answers on the device
            API&rsquo;s own host, so a device that can provision can also reach the relay.
          </li>
          <li>
            <strong>Its client carries the relay.</strong> One test build of Android Util does. The section on which
            clients carry the relay lists the others.
          </li>
          <li>
            <strong>The device API has given it a relay address.</strong> The device API adds the address to its
            provision answer for the devices named in the relay policy. With no policy, no device gets an address
            and the relay only waits.
          </li>
        </ul>
        <p className="doc-p">
          A device on UDP never contacts the relay. Switching the relay on changes nothing for a device whose
          tunnel works.
        </p>

        <h2 className="doc-h2" id="how-the-relay-path-works">How the relay path works</h2>
        <p className="doc-p">
          The relay runs in the MDM clusters, where the device API runs, and answers on the device API&rsquo;s own
          host names on port 443. A device that has provisioned has already reached that host through the
          customer&rsquo;s firewall. Falling back to the same host and port is therefore safe, and the customer adds
          no firewall rule for the relay.
        </p>
        <p className="doc-p">
          The relay client inside Android Util opens a WebSocket over TLS to that host. The cluster&rsquo;s ingress
          sends provision requests to the device API and the WebSocket to a relay pod. WireGuard on the device sends
          its packets to the relay client on the device, where it used to send them to the hub. The relay sends each
          one to the hub over UDP, and the hub&rsquo;s replies come back the same way.
        </p>
        <DocTopology
          caption="The relay inside the MDM cluster, next to the device API, 14 October 2026"
          groups={[
            { id: 'Mdm', label: 'MDM cluster', note: 'New York' },
            { id: 'Hubs', label: 'Hubs' },
          ]}
          nodes={[
            { id: 'Dev', label: 'Android player', note: 'UDP 443 blocked' },
            { id: 'Host', label: 'Device API host', note: 'shared ingress', group: 'Mdm' },
            { id: 'Api', label: 'Device API', note: 'provisioning', group: 'Mdm' },
            { id: 'Relay', label: 'TCP relay', note: '4 pods', group: 'Mdm' },
            { id: 'Us', label: 'US hub', group: 'Hubs' },
            { id: 'Eu', label: 'EU hub', group: 'Hubs' },
            { id: 'Apac', label: 'APAC hub', group: 'Hubs' },
          ]}
          edges={[
            { from: 'Dev', to: 'Host', text: 'TCP 443' },
            { from: 'Host', to: 'Api', text: 'provision' },
            { from: 'Host', to: 'Relay', text: 'WebSocket' },
            { from: 'Relay', to: 'Hubs', text: 'UDP 443' },
          ]}
        />
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Part</th>
                <th>Where it runs</th>
                <th>What it does</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Relay client</strong></td>
                <td>Inside Android Util, on the device</td>
                <td>Opens the WebSocket and passes WireGuard packets between it and the tunnel on the device.</td>
              </tr>
              <tr>
                <td><strong>Device API host</strong></td>
                <td>The ingress of each MDM cluster</td>
                <td>
                  Serves the device API and the relay on one host name and port 443. It sends the relay path to the
                  relay and every other request to the device API.
                </td>
              </tr>
              <tr>
                <td><strong>TCP relay</strong></td>
                <td>
                  In the MDM clusters in New York, next to the device API. Four pods in each of the two production
                  clusters
                </td>
                <td>Sends each WebSocket message to the hub as one UDP packet, and each reply back.</td>
              </tr>
              <tr>
                <td><strong>Hub</strong></td>
                <td>One per region, unchanged</td>
                <td>Sees an ordinary WireGuard peer whose packets arrive from the relay&rsquo;s address.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <ul className="doc-list">
          <li>
            <strong>The relay cannot read the tunnel.</strong> It holds no WireGuard key and forwards encrypted
            packets. The hub drops any packet that a known peer&rsquo;s key did not seal.
          </li>
          <li>
            <strong>One message is one packet.</strong> A binary WebSocket message carries one WireGuard packet in
            each direction, up to 65,535 bytes.
          </li>
          <li>
            <strong>The device names a region, never a host.</strong> The relay address ends in <code>us</code>,{' '}
            <code>eu</code> or <code>apac</code>, and the relay holds one fixed hub address for each. The device API
            fills in the region of the hub that holds the device&rsquo;s peer.
          </li>
          <li>
            <strong>The relay keeps nothing between sessions.</strong> Each WebSocket gets its own UDP socket to the
            hub. A reconnect can land on another pod, and the hub follows the peer to the new source address.
          </li>
          <li>
            <strong>The tunnel uses smaller packets on the relay.</strong> Android Util sets the tunnel MTU to 1,280
            bytes on the relay and leaves the default on UDP.
          </li>
          <li>
            <strong>A silent session ends after 2 minutes.</strong> A live tunnel sends a keepalive every 25
            seconds, so only a dead session reaches that limit.
          </li>
        </ul>

        <h2 className="doc-h2" id="how-a-device-switches-and-comes-back">How a device switches and comes back</h2>
        <p className="doc-p">
          Android Util decides by itself, from the age of its last WireGuard handshake. The checks are the ones{' '}
          <Link to="/docs/securelink">SecureLink</Link> describes under how Android Util recovers.
        </p>
        <DocSequence
          caption="From a blocked UDP handshake to the relay, and the hourly try to return"
          participants={[
            { id: 'Util', label: 'Android Util', note: 'on the device' },
            { id: 'Api', label: 'Device API' },
            { id: 'Relay', label: 'TCP relay' },
            { id: 'Hub', label: 'Regional hub' },
          ]}
          steps={[
            { from: 'Util', to: 'Hub', text: 'Handshake over UDP 443' },
            { kind: 'note', over: ['Util', 'Hub'], text: 'No reply. The network drops UDP 443' },
            { from: 'Util', to: 'Util', text: 'Restart the tunnel twice' },
            { from: 'Util', to: 'Api', text: 'Provision again, same key' },
            { kind: 'reply', from: 'Api', to: 'Util', text: 'Peer and relay address' },
            { from: 'Util', to: 'Relay', text: 'Open signed WebSocket, TCP 443' },
            { from: 'Relay', to: 'Hub', text: 'Forward each packet, UDP 443' },
            { kind: 'reply', from: 'Hub', to: 'Relay', text: 'Reply packets' },
            { kind: 'reply', from: 'Relay', to: 'Util', text: 'Reply packets' },
            { kind: 'note', over: ['Util', 'Hub'], text: 'The tunnel is up through the relay' },
            { from: 'Util', to: 'Hub', text: 'After 60 minutes, handshake over UDP' },
            {
              kind: 'note',
              over: ['Util', 'Hub'],
              text: 'A reply within 30 seconds keeps UDP. No reply returns to the relay',
            },
          ]}
        />
        <ul className="doc-list">
          <li>
            <strong>The switch happens on the third stale check.</strong> Android Util checks every 60 seconds and
            calls a handshake older than 180 seconds stale. It restarts the tunnel on the first and the second stale
            check. On the third it switches to the relay when it holds a relay address.
          </li>
          <li>
            <strong>The first switch takes longer.</strong> A device with no stored address provisions again on the
            third stale check, and the answer carries the address. The device then pauses for 300 seconds and
            switches on a later round. It stores the address, so a later switch skips steps 3 and 4.
          </li>
          <li>
            <strong>It tries UDP again every 60 minutes.</strong> The hour counts from the switch, and from each try
            that fails. A try stops the relay path and waits 30 seconds for a UDP handshake. A handshake keeps the
            device on UDP. Without one the device returns to the relay.
          </li>
          <li>
            <strong>A relay that stops working sends the device back to UDP.</strong> After two restarts on the relay
            with no handshake, Android Util returns to UDP and provisions again. On a network that carries neither
            path it alternates between them, once per recovery round.
          </li>
          <li>
            <strong>A dropped WebSocket reconnects by itself.</strong> The first retry comes within 1 second. Each
            later retry waits twice as long, up to 120 seconds. Packets sent during the gap are lost, and WireGuard
            sends them again.
          </li>
          <li>
            <strong>A provision answer without an address ends the relay for that device.</strong> Android Util
            deletes the stored address and returns to UDP.
          </li>
        </ul>

        <h2 className="doc-h2" id="who-is-allowed-onto-the-relay">Who is allowed onto the relay</h2>
        <p className="doc-p">Two checks apply, in two places.</p>
        <p className="doc-p">
          <strong>The device API decides which devices get the address.</strong> The relay policy holds a list of
          device UUIDs and a percentage of the fleet. A device gets the address when its UUID is on the list or
          falls inside the percentage. The percentage is computed from the UUID, so the same devices stay included
          as the number grows. On 14 October 2026 the production policy lists two lab devices and its percentage is
          0.
        </p>
        <ul className="doc-list">
          <li>A policy change reaches new provisions within about 10 seconds.</li>
          <li>
            Removing the policy stops the address at each device&rsquo;s next provision. A device already on the
            relay stays there until then.
          </li>
          <li>When the device API cannot read the policy, no device gets an address.</li>
        </ul>
        <p className="doc-p">
          <strong>The relay checks a device signature on every connection.</strong> Android Util signs each
          WebSocket request with the scheme it uses for the device API. It signs again on every reconnect.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>The relay answers</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>401</code></td>
                <td>The request carries no valid device signature. This check runs first.</td>
              </tr>
              <tr>
                <td><code>404</code></td>
                <td>The address names a region the relay does not serve.</td>
              </tr>
              <tr>
                <td><code>503</code></td>
                <td>The pod already holds 5,000 sessions, or it is shutting down.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">Android Util treats each of these as a failed connection and retries.</p>

        <h2 className="doc-h2" id="which-clients-carry-the-relay">Which clients carry the relay</h2>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Build</th>
                <th>On a network that drops UDP 443</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Android Util</strong></td>
                <td>4.56.97, a test build</td>
                <td>Switches to the relay.</td>
              </tr>
              <tr>
                <td><strong>Android Util</strong></td>
                <td>4.55</td>
                <td>Stays <strong>provisioned</strong> with no tunnel.</td>
              </tr>
              <tr>
                <td><strong>Pro Util</strong></td>
                <td>3.0.10.P27</td>
                <td>No tunnel. It has no relay client.</td>
              </tr>
              <tr>
                <td><strong>OptiEdge agent</strong></td>
                <td>v0.3.10-staff-alpha</td>
                <td>No tunnel. It has no relay client.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          Build 4.56.97 is installed on lab devices only. No released build of Android Util carries the relay on 14
          October 2026.
        </p>

        <h2 className="doc-h2" id="what-to-expect-on-the-relay">What to expect on the relay</h2>
        <p className="doc-p">
          The figures come from lab devices in Vietnam on the APAC hub, on 12 to 14 October 2026. No US or EU device
          has been measured on the relay.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Event</th>
                <th>What the device does</th>
                <th>Measured</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>The network starts dropping UDP 443</td>
                <td>Reaches the relay after about 14 minutes the first time</td>
                <td>845 s and 867 s, two production devices</td>
              </tr>
              <tr>
                <td>The network allows UDP 443 again</td>
                <td>Returns to UDP at its next hourly try</td>
                <td>3,490 s; both relay sessions lasted 3,599 s</td>
              </tr>
              <tr>
                <td>An hourly try fails</td>
                <td>Has no tunnel for about half a minute, then is back on the relay</td>
                <td>36 s, two production devices</td>
              </tr>
              <tr>
                <td>A relay pod restarts</td>
                <td>Reconnects to another pod</td>
                <td>16 s without replies, one device</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          A switch with a stored address skips the provision and the pause. It has not been timed on a build with
          production timers.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Measure</th>
                <th>Through the relay</th>
                <th>Direct over UDP</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Round-trip time</td>
                <td>About 550 ms</td>
                <td>81 to 95 ms</td>
              </tr>
              <tr>
                <td>TCP throughput inside the tunnel</td>
                <td>0.6 to 1.0 Mbit/s</td>
                <td>5.4 Mbit/s</td>
              </tr>
              <tr>
                <td>Remote Control, WireGuard view: first picture</td>
                <td>9 to 18 s, or none in 60 s</td>
                <td>About 5 s</td>
              </tr>
              <tr>
                <td>Remote Control, WireGuard view: screen updates</td>
                <td>0.1 a second in four runs, 1.0 in one</td>
                <td>2.9 a second</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          Over one hour on 14 October 2026, two production devices on the relay answered 99% of pings sent once a
          second. Each lost 36 in a row during its failed hourly try, and one lost a single other ping.
        </p>
        <aside className="doc-note doc-note--warn">
          <span className="doc-note-label">Warning</span>
          <p>
            Remote Control still opens the WireGuard view for a device on the relay. For a device on the APAC hub
            that view barely moves, because every packet crosses to New York and back. The RealVNC view worked on the
            same device: its first picture came after 11 s, at 1.5 updates a second.
          </p>
        </aside>
        <p className="doc-p">
          The relay itself was load-tested on 12 October 2026 with three pods. It held 10,000 sessions for 30
          minutes with no session closed. A rolling restart closed every session over 5 minutes, and each
          reconnected within about 1 second. Four pods at 5,000 sessions each give one production cluster 20,000
          seats.
        </p>

        <h2 className="doc-h2" id="known-limits">Known limits</h2>
        <ul className="doc-list">
          <li>
            <strong>Every relay runs in New York.</strong> A device on the APAC or EU hub sends its traffic to New
            York and from there to its hub. Round-trip time for an APAC device goes from under 100 ms to about 550
            ms.
          </li>
          <li>
            <strong>Pro Util and the OptiEdge agent have no relay client.</strong> A ProPlayer on a network that
            drops UDP 443 stays without a tunnel.
          </li>
          <li>
            <strong>An ingress reload closes every relay session.</strong> The device API hosts close all WebSockets
            240 seconds after their ingress reloads. Each relayed device reconnects by itself.
          </li>
          <li>
            <strong>The relay address names one host.</strong> Every relayed device connects to the cluster behind
            that host. The other cluster&rsquo;s pods stay idle until the policy names its host.
          </li>
        </ul>

        <aside className="doc-next">
          <div>
            <h2 className="doc-next-title">Checking one device?</h2>
            <p>Network → Peers shows how long ago each peer last completed a handshake.</p>
          </div>
          <Link className="btn" to="/vpn/peers">
            Open Peers
          </Link>
        </aside>

        <p className="doc-back">
          <a href="#top">Back to top ↑</a>
        </p>
      </div>
      <DocsToc items={TOC} />
    </>
  );
}
